import { useEffect, useMemo, useRef, useState } from "react"
import { Grid } from "@mui/material"
import { DateTime } from "luxon"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { ApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { ActionBarItem, FloatingActionBar } from "../../../../../../shared/components/table/FloatingActionBar"
import { useSupplier } from "../../../../supplierHooks"
import { SUPPLIER_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { fetchMerchantSales, SupplierMerchantSalesQuery } from "../merchantSalesApi"
import { CurrencyCode, MerchantSalesMerchants } from "../merchantSalesTypes"
import { DetailedPanel } from "../components/DetailedPanel"
import { SelectShopType } from "../components/SelectShopType"
import { useExportMerchantSales } from "../hooks/useExportMerchantSales"
import { TableSummaryWrap, TableTotalAmountFromAPIBox } from "../../../../../../shared/components/table/TableSummaryBox"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useColumnFactory } from "../../../../../../core/api/useTableColumns"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardPaymentMethodsFilter } from "../../../../../../shared/components/filters/components/StandardPaymentMethodsFilter"
import { useOrderTypeFilter } from "../../../../../../shared/components/filters/orderTypeFilter/useOrderTypeState"
import { StandardOrderTypeFilter } from "../../../../../../shared/components/filters/orderTypeFilter/StandardOrderTypeFilter"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { orderFilterButtonConfig } from "../../../../../../shared/components/filters/orderTypeFilter/helpers"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

export function SupplierMerchantSales() {
    const { supplierId } = useSupplier()

    const { l10n } = useAppLocalization()

    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const { orderType, setOrderType } = useOrderTypeFilter()
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const [shopTypes, setShopTypes] = useState<Array<string>>([])
    const [paymentMethods, setPaymentMethods] = useState<Array<string>>([])

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [selectedRows, setSelectedRows] = useState<MerchantSalesMerchants[]>()

    const tableInstanceRef = useRef<MRT_TableInstance<MerchantSalesMerchants>>(null)

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const merchantSalesResult = useApiQuery(fetchMerchantSales, {
        queryName: SupplierMerchantSalesQuery.MERCHANT_SALES_FOR_SUPPLIER,
        dependencies: {
            shopTypes: shopTypes.join(","),
            paymentMethods: paymentMethods.join(","),
            supplierId,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
            orderType,
            dateType,
        },
    })

    const rowData = useMemo(() => merchantSalesResult.data?.merchants ?? [], [merchantSalesResult?.data?.merchants])

    const { handleExportRows } = useExportMerchantSales(dateState.startDate.toISODate(), dateState.endDate.toISODate())
    const columnFactory = useColumnFactory<MerchantSalesMerchants>()

    const columns = useMemo<MRT_ColumnDef<MerchantSalesMerchants>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-merchant-sales-table-red-no-column"),
                accessorKey: "caseNumber",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-merchant-sales-table-name-column"),
                accessorKey: "name",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell title={row.original.name} subtitle1={row.original.address.displayName} />
                ),
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("supplier-merchant-sales-table-upcoming-sales-column", {
                        currencyCode: CurrencyCode.DKK,
                    }),
                },
                (row) => row.upcomingSales,
            ),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("supplier-merchant-sales-table-booked-sales-column", {
                        currencyCode: CurrencyCode.DKK,
                    }),
                },
                (row) => row.bookedSales,
            ),
        ],
        [l10n, columnFactory],
    )

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    return (
        <>
            <LocalizedStrict id="supplier-merchant-sales-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPLIER_MERCHANT_SALES} title="Merchant Sales">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-merchant-sales">
                            <Breadcrumb>Merchant sales</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <StandardOrderTypeFilter
                orderType={orderType}
                setOrderType={setOrderType}
                config={orderFilterButtonConfig}
            />

            <Grid container direction="column">
                <ApiQueryTable
                    isLoading={merchantSalesResult.isInitialLoading}
                    isFetching={merchantSalesResult.isFetching}
                    isError={merchantSalesResult.isError}
                    data={rowData}
                    tableInstanceRef={tableInstanceRef}
                    enableExpandAll={false}
                    enableExpanding
                    columns={columns}
                    getRowId={(originalRow, index) => {
                        if (!originalRow.id) return index.toString()

                        return originalRow.id.toString()
                    }}
                    state={{
                        rowSelection,
                    }}
                    renderDetailPanel={({ row }) => (
                        <DetailedPanel merchantId={row.original.id} shopTypes={shopTypes} />
                    )}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table}>
                            <StandardExportButton onClick={() => handleExportRows()} />
                        </StandardTableFilter>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                            <SelectShopType shopTypes={shopTypes} setShopTypes={setShopTypes} />
                            <StandardPaymentMethodsFilter
                                value={paymentMethods}
                                setValue={setPaymentMethods}
                                size="small"
                            />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={() => (
                        <TableSummaryWrap>
                            <TableTotalAmountFromAPIBox totalAmount={merchantSalesResult.data?.totalBookedSales} />
                        </TableSummaryWrap>
                    )}
                />
            </Grid>

            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict
                        id="supplier-merchant-sales-table-floating-action-bar-export"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem
                            icon={faFileArrowDown}
                            title="Export"
                            onClick={() =>
                                handleExportRows(
                                    tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original.id),
                                )
                            }
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}
        </>
    )
}
